import { useEffect, useRef, useState } from "react";
import { api, imgUrl } from "@/lib/api";
import { X, ArrowUpRight, Maximize2 } from "lucide-react";

const yearOf = (p) => {
  if (p.year) return String(p.year);
  const m = `${p.source || ""} ${p.title || ""}`.match(/\b(?:19|20)\d{2}\b/);
  if (m) return m[0];
  if (p.created_at) return String(p.created_at).slice(0, 4);
  return "—";
};

function PressCard({ p, onOpen }) {
  if (p.image) {
    return (
      <button
        onClick={onOpen}
        className="group surface overflow-hidden text-left block w-full hover:border-[color:var(--gold)]/40 transition-all duration-300 hover:-translate-y-1"
        data-testid={`press-item-${p.id}`}
      >
        <div className="h-64 overflow-hidden bg-black/40 relative">
          <img
            src={imgUrl(p.image)}
            alt={p.title || p.source}
            loading="lazy"
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
            <Maximize2
              size={26}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: "var(--gold)" }}
            />
          </div>
        </div>
        <div className="p-4">
          <p className="eyebrow" style={{ color: "var(--gold)" }}>
            {p.source}
          </p>
          <h3 className="font-serif text-base tracking-tight mt-1 leading-snug">
            {p.title}
          </h3>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-2 inline-block">
            Click to read &rarr;
          </span>
        </div>
      </button>
    );
  }
  const Wrapper = p.href ? "a" : "div";
  const props = p.href
    ? { href: p.href, target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrapper
      {...props}
      className="group surface p-5 flex items-start justify-between gap-3 hover:border-[color:var(--gold)]/40 transition-all block"
      data-testid={`press-item-${p.id}`}
    >
      <div>
        <p className="eyebrow" style={{ color: "var(--gold)" }}>
          {p.source}
        </p>
        <h3 className="font-serif text-lg tracking-tight mt-1 group-hover:text-[color:var(--gold)] transition-colors">
          {p.title}
        </h3>
      </div>
      {p.href && (
        <ArrowUpRight
          size={15}
          className="text-white/30 group-hover:text-[color:var(--gold)] shrink-0 mt-1 transition-colors"
        />
      )}
    </Wrapper>
  );
}

function Lightbox({ p, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] flex flex-col items-center justify-center p-4 sm:p-8"
      style={{ background: "rgba(5,3,8,0.92)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="press-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 text-white/70 hover:text-white z-10"
        data-testid="press-lightbox-close"
      >
        <X size={26} />
      </button>
      <img
        src={imgUrl(p.image)}
        alt={p.title || p.source}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[94vw] max-h-[82vh] object-contain shadow-2xl"
      />
      <div
        className="mt-4 text-center max-w-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="press-lightbox-caption"
      >
        {(() => {
          const parts = (p.source || "").split("\u00b7").map((s) => s.trim()).filter(Boolean);
          const pub = parts[0] || p.source;
          const date = parts.slice(1).join(" \u00b7 ") || (yearOf(p) !== "\u2014" ? yearOf(p) : "");
          return (
            <>
              <p className="eyebrow" style={{ color: "var(--gold)" }}>
                {pub}
              </p>
              {date && (
                <p className="text-white/55 text-[11px] uppercase tracking-[0.28em] mt-1.5">
                  {date}
                </p>
              )}
              {p.title && (
                <p className="text-white/75 text-sm mt-2 leading-relaxed">{p.title}</p>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

function YearGroup({ year, items, onOpen }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative pl-7 sm:pl-10 border-l border-white/10"
      data-testid={`press-year-${year}`}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(28px)",
        transition: "opacity 0.7s ease, transform 0.7s cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      <div
        className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full ring-4 ring-[#0a050d]"
        style={{ background: "var(--gold)" }}
      />
      <div
        className="font-serif text-3xl sm:text-4xl tracking-tighter font-light mb-6"
        style={{ color: "var(--gold)" }}
      >
        {year}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {items.map((p) => (
          <PressCard key={p.id} p={p} onOpen={() => onOpen(p)} />
        ))}
      </div>
    </div>
  );
}

export function PressTimeline({ items }) {
  const [fetched, setFetched] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    if (items) return;
    api
      .get("/public/press")
      .then((r) => setFetched(r.data.items || []))
      .catch(() => {});
  }, [items]);

  const press = items || fetched;
  if (!press.length) return null;

  const CHIPS = ["All", "Music", "Community", "Feature"];
  const visible =
    filter === "All"
      ? press
      : press.filter((p) => (p.category || "Feature") === filter);

  const groups = {};
  visible.forEach((p) => {
    const y = yearOf(p);
    (groups[y] = groups[y] || []).push(p);
  });
  const years = Object.keys(groups).sort();

  return (
    <div data-testid="press-timeline">
      <div className="flex flex-wrap gap-2 mb-10">
        {CHIPS.map((c) => {
          const active = filter === c;
          return (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] rounded-full border transition-all"
              style={
                active
                  ? { background: "var(--gold)", color: "#0a050d", borderColor: "var(--gold)" }
                  : { background: "transparent", color: "rgba(255,255,255,0.6)", borderColor: "rgba(255,255,255,0.15)" }
              }
              data-testid={`press-filter-${c.toLowerCase()}`}
            >
              {c}
            </button>
          );
        })}
      </div>

      {years.length === 0 ? (
        <p className="text-white/40 text-sm italic" data-testid="press-filter-empty">
          No coverage in this category yet.
        </p>
      ) : (
        <div className="space-y-14">
          {years.map((year) => (
            <YearGroup
              key={year}
              year={year}
              items={groups[year]}
              onOpen={setLightbox}
            />
          ))}
        </div>
      )}
      {lightbox && <Lightbox p={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

export default PressTimeline;
